import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

export const useS3Upload = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { token } = useAuth();
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  const uploadFile = async (file, options = {}) => {
    const {
      folderType = null, // Let backend auto-detect
      customFilename = null,
      path = null, // Path within the folder type
      onProgress = null
    } = options;

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const params = new URLSearchParams();
      if (folderType) params.append('folder_type', folderType);
      
      // If path is provided, construct custom filename with path
      let finalCustomFilename = customFilename;
      if (path && !customFilename) {
        // Use original filename with path prefix
        finalCustomFilename = `${path}/${file.name}`;
      } else if (path && customFilename) {
        // Use custom filename with path prefix
        finalCustomFilename = `${path}/${customFilename}`;
      }
      
      if (finalCustomFilename) params.append('custom_filename', finalCustomFilename);
      
      const url = `${API_BASE_URL}/api/s3/upload${params.toString() ? `?${params.toString()}` : ''}`;

      const xhr = new XMLHttpRequest();
      
      return new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(progress);
            if (onProgress) onProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            try {
              const result = JSON.parse(xhr.responseText);
              setUploading(false);
              setUploadProgress(100);
              resolve(result);
            } catch (error) {
              setUploading(false);
              reject(new Error('Invalid response format'));
            }
          } else {
            setUploading(false);
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData.detail || 'Upload failed'));
            } catch {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          }
        });

        xhr.addEventListener('error', () => {
          setUploading(false);
          reject(new Error('Network error during upload'));
        });

        xhr.open('POST', url);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
      });

    } catch (error) {
      setUploading(false);
      throw error;
    }
  };

  const uploadMultipleFiles = async (files, options = {}) => {
    const { folderType = null } = options;

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const params = new URLSearchParams();
      if (folderType) params.append('folder_type', folderType);
      
      const url = `${API_BASE_URL}/api/s3/upload/multiple${params.toString() ? `?${params.toString()}` : ''}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Multiple upload failed');
      }

      const results = await response.json();
      setUploading(false);
      setUploadProgress(100);
      return results;

    } catch (error) {
      setUploading(false);
      throw error;
    }
  };

  const deleteFile = async (s3Key) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/s3/files/${encodeURIComponent(s3Key)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Delete failed');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  };

  const listFiles = async (folderType = null, limit = 100) => {
    try {
      const params = new URLSearchParams();
      if (folderType) params.append('folder_type', folderType);
      params.append('limit', limit.toString());

      const response = await fetch(`${API_BASE_URL}/api/s3/files?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to list files');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  };

  const getPresignedUrl = async (s3Key, expiration = 3600) => {
    try {
      const params = new URLSearchParams();
      params.append('expiration', expiration.toString());

      const response = await fetch(`${API_BASE_URL}/api/s3/presigned-url/${encodeURIComponent(s3Key)}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to get presigned URL');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  };

  return {
    uploadFile,
    uploadMultipleFiles,
    deleteFile,
    listFiles,
    getPresignedUrl,
    uploading,
    uploadProgress
  };
};

export default useS3Upload;